import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';

const NavigationSidebar = ({ isCollapsed = false, onToggleCollapse }) => {
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { t, currentLanguage } = useLanguage();

  const navigationSections = [
    {
      title: t('nav.dashboard'),
      items: [
        {
          label: t('nav.dashboard'),
          path: '/agency-dashboard',
          icon: 'LayoutDashboard',
          roles: ['agency_admin', 'travel_agent', 'accountant']
        },
        {
          label: t('nav.dashboard'),
          path: '/super-admin-dashboard',
          icon: 'Shield',
          roles: ['super_admin']
        }
      ]
    },
    {
      title: t('nav.pilgrims'),
      items: [
        {
          label: t('pilgrims.pilgrimDetails'),
          path: '/pilgrim-profile',
          icon: 'UserCircle',
          roles: ['agency_admin', 'travel_agent']
        },
        {
          label: t('pilgrims.title'),
          path: '/pilgrim-management',
          icon: 'Users',
          roles: ['agency_admin', 'travel_agent']
        }
      ]
    },
    {
      title: t('nav.groups'),
      items: [
        {
          label: t('groups.title'),
          path: '/group-management',
          icon: 'UsersRound',
          roles: ['agency_admin', 'travel_agent']
        },
        {
          label: t('visas.title'),
          path: '/visa-management',
          icon: 'FileText',
          roles: ['agency_admin', 'travel_agent']
        }
      ]
    },
    {
      title: t('dashboard.quickActions'),
      items: [
        {
          label: t('financial.title'),
          path: '/financial-management',
          icon: 'DollarSign',
          roles: ['agency_admin', 'accountant']
        },
        {
          label: t('documents.title'),
          path: '/document-management',
          icon: 'FolderOpen',
          roles: ['agency_admin', 'travel_agent']
        },
        {
          label: t('communication.title'),
          path: '/communication-center',
          icon: 'MessageSquare',
          roles: ['agency_admin', 'travel_agent']
        },
        {
          label: t('analytics.title'),
          path: '/analytics-dashboard',
          icon: 'BarChart3',
          roles: ['agency_admin', 'super_admin']
        }
      ]
    },
    {
      title: t('nav.settings'),
      items: [
        {
          label: currentLanguage === 'ar' ? 'إدارة الوكالات' : 'Gestion des agences',
          path: '/agency-management',
          icon: 'Users',
          roles: ['super_admin']
        },
        {
          label: t('nav.agencies'),
          path: '/agency-registration',
          icon: 'Building2',
          roles: ['super_admin']
        },
        {
          label: t('nav.reports'),
          path: '/reports',
          icon: 'BarChart3',
          roles: ['agency_admin', 'super_admin']
        },
        {
          label: t('nav.settings'),
          path: '/settings',
          icon: 'Settings',
          roles: ['agency_admin', 'super_admin']
        },
        {
          label: currentLanguage === 'ar' ? 'الملف الشخصي' : 'Mon profil',
          path: '/user-profile-settings',
          icon: 'UserCog',
          roles: ['agency_admin', 'super_admin', 'travel_agent', 'accountant']
        }
      ]
    }
  ];

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const handleMobileToggle = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const handleMobileClose = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      <button
        onClick={handleMobileToggle}
        className="fixed top-4 left-4 z-[60] lg:hidden p-2 rounded-md bg-card shadow-warm transition-smooth hover:shadow-warm-md border border-border"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>
      <aside
        className={`
          fixed lg:fixed top-0 left-0 h-full bg-card border-r border-border shadow-warm-md z-[50]
          transition-smooth flex flex-col
          ${isCollapsed ? 'w-20' : 'w-72'}
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="sidebar-header flex-shrink-0">
          <div className="flex items-center">
            <div className="sidebar-logo">
              <Icon name="Compass" size={24} className="sidebar-logo-icon" />
            </div>
            {!isCollapsed && (
              <span className="sidebar-logo-text">OmraHajjManager</span>
            )}
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 space-y-6">
          {navigationSections?.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {!isCollapsed && (
                <h3 className="caption text-muted-foreground uppercase tracking-wider mb-3 px-3">
                  {section?.title}
                </h3>
              )}
              <ul className="space-y-1">
                {section?.items?.map((item, itemIndex) => (
                  <li key={itemIndex}>
                    <Link
                      to={item?.path}
                      onClick={handleMobileClose}
                      className={`
                        flex items-center gap-3 px-3 py-3 rounded-md transition-smooth
                        ${isActiveRoute(item?.path)
                          ? 'bg-primary text-primary-foreground shadow-warm'
                          : 'text-foreground hover:bg-muted hover:shadow-warm-sm'
                        }
                        ${isCollapsed ? 'justify-center' : ''}
                      `}
                      title={isCollapsed ? item?.label : ''}
                    >
                      <Icon 
                        name={item?.icon} 
                        size={20} 
                        className="flex-shrink-0"
                      />
                      {!isCollapsed && (
                        <span className="font-medium">{item?.label}</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {onToggleCollapse && (
          <div className="hidden lg:block p-4 border-t border-border flex-shrink-0">
            <button
              onClick={onToggleCollapse}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-muted hover:bg-muted/80 transition-smooth"
              aria-label={isCollapsed ? t('common.expand') : t('common.collapse')}
            >
              <Icon 
                name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} 
                size={20} 
              />
              {!isCollapsed && <span className="caption">{t('common.collapse')}</span>}
            </button>
          </div>
        )}
      </aside>
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[40] lg:hidden"
          onClick={handleMobileClose}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default NavigationSidebar;